import {TFunction} from 'i18next';
import {
    filter, isEmpty, find, map, some, sortBy, uniqBy, isArray, keys,
    mergeWith, transform, last, forEach, isNumber, includes
} from 'lodash';
import moment from 'moment';

import {agentActionsApi} from 'api/agent-actions';
import {IconName, UserParticipantIds} from 'components';
import {ROUTE_PATH} from 'constants/routes';
import {
    AGENT_ACTIVE_SESSION_STATUSES, ACTIVITY_ICONS, SystemEndSessionCode, USER_PARTICIPANT_ROLES, DATA_PARTICIPANT_ID,
    INVITEE_PARTICIPANT_ROLES
} from 'constants/session';
import {history} from 'context/history';
import {SessionTextChatMessageOwner, WebRTCServerSource} from 'enums';
import {
    SessionActivityName, Session, AnyParticipant, ClientParticipant, SessionStatus, SessionResultStatus, ClientDevice, SessionHistory
} from 'interfaces';
import {
    AgentParticipant, WebRTCActivityData, AsyncRequestStatusName, AsyncRequestTypeName, OnlineSession, ScreenSharingActivityStatus,
    SessionDynamicData, SessionResult, SessionActivity, SessionActivityTextChatData,
    AgentSession, SessionAppointment, UserParticipant, GuestParticipant, InviteeParticipant
} from 'interfaces/session';
import {TRANSLATION_KEYS} from 'locale';
import {onlineSessionsStore} from 'services';
import {
    DefaultParticipant, OnlineStatus, ParticipantRole, UserParticipantRole,
    TextChatMessage, TextChatThreads, User
} from 'types';

import {transformUtils} from './transform';
import {userAuthUtils} from './user-auth';

const AWAY_AFTER = 15; // 15 seconds
const OFFLINE_AFTER = 30; // 30 seconds

type GetParticipantConditions = {
    participantId?: string,
    role?: ParticipantRole,
    roles?: ParticipantRole[],
    eid?: number
}

export const getWebRTCRoomName = (session: Session<AnyParticipant>) => {
    if (!session || session.status !== SessionStatus.InSession) {
        return null;
    }
    const webRTCActivity = find(session.activities, (activity) => [
        SessionActivityName.ScreenSharing,
        SessionActivityName.AudioVideo,
        SessionActivityName.Audio
    ].includes(activity.name));

    if (!webRTCActivity) {
        return null;
    }

    return (webRTCActivity.data as WebRTCActivityData)?.server?.roomName;
};

const hasActivityWithAudio = (session: Session<AnyParticipant>) =>
    session?.status === SessionStatus.InSession && some(session.activities, (activity) => [
        SessionActivityName.AudioVideo,
        SessionActivityName.Audio,
        SessionActivityName.Avatar
    ].includes(activity.name));

const filterWebRTCActivitiesBySource = (activities: SessionActivity<WebRTCActivityData>[], source: WebRTCServerSource) =>
    filter(activities, (a) => a?.data?.server?.source === source);

export const getActivityIcon = (name: SessionActivityName): IconName => ACTIVITY_ICONS[name] || 'x-circle';

export const getScreenSharingPresenterId = (session: Session<AnyParticipant>) => {
    if (!session || session.status !== SessionStatus.InSession) {
        return null;
    }
    const screenSharingActivity = find(session.activities, {name: SessionActivityName.ScreenSharing});
    return screenSharingActivity?.data?.presenterId;
};

const getVideoMinimized = (session: Session<AnyParticipant>) => {
    const coBrowsingActivity = find(session?.activities, {name: SessionActivityName.CoBrowsing});
    const screenSharingActivity = find(session?.activities, {name: SessionActivityName.ScreenSharing});
    const screenSharingActivityStatus = screenSharingActivity?.data?.status;
    return !!coBrowsingActivity ||
                (screenSharingActivityStatus === ScreenSharingActivityStatus.Accepted &&
                    getScreenSharingPresenterId(session) !== session?.localParticipant?.id);
};

const getParticipant = <T extends AnyParticipant>(
    session: {participants: AnyParticipant[]},
    conditions: GetParticipantConditions,
): T => {
    const {participantId: id, role, roles, eid} = conditions;
    const participant = find(session?.participants, (p) => {
        if (id && p.id !== id) {
            return false;
        }
        if (role && p.role !== role) {
            return false;
        }
        if (roles && !includes(roles, p.role)) {
            return false;
        }
        if (eid && (p as UserParticipant).data.eid !== eid) {
            return false;
        }
        return true;
    });
    return participant as T;
};

const getParticipants = <T extends AnyParticipant[] = AnyParticipant[]>(
    session: {participants: AnyParticipant[]},
    conditions: GetParticipantConditions,
): T => {
    const {participantId: id, role, roles, eid} = conditions;
    const participants = filter(session?.participants, (participant) => {
        if (id && participant.id !== id) {
            return false;
        }
        if (role && participant.role !== role) {
            return false;
        }
        if (roles && !includes(roles, participant.role)) {
            return false;
        }
        if (eid && (participant as UserParticipant).data.eid !== eid) {
            return false;
        }
        return true;
    });

    return participants as T;
};

const getUserParticipantIdGroups = (session: {participants: AnyParticipant[]}): UserParticipantIds => {
    const participants = getParticipants(session, {roles: [ParticipantRole.Agent, ParticipantRole.Assistant]});
    const userParticipantIds = transform(participants, (acc, participant) => {
        if ([ParticipantRole.Agent, ParticipantRole.Assistant].includes(participant.role)) {
            acc[participant.role].push(participant.id);
        }
    }, {[UserParticipantRole.Agent]: [], [UserParticipantRole.Assistant]: []});
    return userParticipantIds;
};

const getParticipantById = (session: Session<AnyParticipant>, id: string) => {
    if (!session) {
        return null;
    }
    return session.localParticipant?.id === id ?
        session.localParticipant :
        find(session.participants, {id}) as AnyParticipant;
};

export const getParticipantByRole = (
    session: {participants: AnyParticipant[], localParticipant?: AnyParticipant}, role: ParticipantRole
) => {
    if (!session || (!session.localParticipant && isEmpty(session.participants))) {
        return null;
    }
    return session.localParticipant?.role === role ?
        session.localParticipant :
        find(session.participants, {role}) as AnyParticipant;
};

export const getClientParticipant = (session: {participants: AnyParticipant[], localParticipant?: AnyParticipant}) =>
    getParticipantByRole(session, ParticipantRole.Client) as ClientParticipant;

export const getGuestParticipant = (session: {participants: AnyParticipant[], localParticipant?: AnyParticipant}) =>
    getParticipantByRole(session, ParticipantRole.Guest) as GuestParticipant;

export const getGuestParticipants = (session: {participants: AnyParticipant[], localParticipant?: AnyParticipant}) =>
    filter(session?.participants, {role: ParticipantRole.Guest}) as GuestParticipant[];

export const getAgentParticipant = (session: {participants: AnyParticipant[], localParticipant?: AnyParticipant}) =>
    getParticipantByRole(session, ParticipantRole.Agent) as AgentParticipant;

export const getDuration = (startDate: Date | string, endDate: Date | string) => {
    if (!startDate || !endDate) {
        return null;
    }
    const hoursDiff = moment(endDate).diff(moment(startDate), 'hours');
    const millisecondsDiff = moment(endDate).diff(moment(startDate));
    const formattedDiff = moment.utc(millisecondsDiff).format('mm:ss');
    return hoursDiff > 0 ? `${hoursDiff}:${formattedDiff}` : formattedDiff;
};

export const getResultFormatted = (t: TFunction, result) => {
    if (typeof result === 'boolean') {
        return result ? t(TRANSLATION_KEYS.COMMON.SESSION.RESULT.SUCCESS) : t(TRANSLATION_KEYS.COMMON.SESSION.RESULT.FAILED);
    }
    return null;
};

export const getSessionResult = (t: TFunction, session: {statuses: SessionStatus[], result: SessionResult}): string => {
    if (map(session.statuses, 'name').includes(SessionStatus.AgentsUnavailable)) {
        return t(TRANSLATION_KEYS.COMMON.SESSION.STATUS.AGENTS_UNAVAILABLE);
    }
    const result = session.result?.status ? session.result.status === SessionResultStatus.Success : null;
    if (typeof result === 'boolean') {
        return getResultFormatted(t, result);
    } else if (session.result?.information === SystemEndSessionCode.Reassigned) {
        return t(TRANSLATION_KEYS.COMMON.SESSION.RESULT.REASSIGNED);
    }
    return null;
};

const isUserParticipant = (participant: AnyParticipant): participant is UserParticipant =>
    USER_PARTICIPANT_ROLES.includes(participant?.role);

const isInviteeParticipant = (participant: AnyParticipant): participant is InviteeParticipant =>
    INVITEE_PARTICIPANT_ROLES.includes(participant?.role);

const getParticipantName = (data: {firstName?: string, lastName?: string}, defaultName = '') => {
    if (!data) {
        return defaultName;
    }
    const {firstName, lastName} = data;
    return filter([firstName, lastName]).join(' ') || defaultName;
};

const getParticipantNames = (participant: AnyParticipant) => {
    if (isInviteeParticipant(participant)) {
        const {firstName, lastName} = participant.data.userInfo;
        return {name: getParticipantName(participant.data.userInfo, participant.data.userInfo?.id), firstName, lastName};
    } else if (isUserParticipant(participant)) {
        const {firstName, lastName} = participant.data;
        return {name: getParticipantName(participant.data), firstName, lastName};
    }
    return null;
};

const PARTICIPANT_ORDER = {
    AGENT: 0,
    ASSISTANT: 10,
    CLIENT: 20,
    GUEST: 30,
    REMOVED: 40
};

const sortParticipantList = (participants: AnyParticipant[]): AnyParticipant[] => sortBy(participants, (p) => {
    if (p.removed) {
        return PARTICIPANT_ORDER.REMOVED;
    }
    if (p.role === ParticipantRole.Agent) {
        return PARTICIPANT_ORDER.AGENT;
    }
    if (p.role === ParticipantRole.Assistant) {
        return PARTICIPANT_ORDER.ASSISTANT;
    }
    if (p.role === ParticipantRole.Client) {
        return PARTICIPANT_ORDER.CLIENT;
    }
    return PARTICIPANT_ORDER.GUEST;
});

const getInitials = (firstName: string, lastName: string, defaultName = '') =>
    (firstName?.[0] || defaultName) + (lastName?.[0] || defaultName);

const computeParticipantName = (participant: AnyParticipant) => {
    if (isInviteeParticipant(participant)) {
        return getParticipantName(participant.data.userInfo, participant.data.userInfo?.id);
    } else if (isUserParticipant(participant)) {
        return getParticipantName(participant.data);
    }
    return null;
};

const appendMeToName = (t: TFunction, name: string, isMe: boolean) =>
    `${name}` + (isMe ? ` (${t(TRANSLATION_KEYS.COMMON.SESSION.LABELS.ME)})` : '');

const computeParticipantNameWithMe = (t: TFunction, participant: AnyParticipant) => {
    const name = computeParticipantName(participant);
    const isMe = isUserParticipant(participant) && participant.data.eid === userAuthUtils.getLocalUser().eid;
    return appendMeToName(t, name, isMe);
};

const getParticipantNameWithMeById = (t: TFunction, session: Session<AnyParticipant>, participantId: string) => {
    const participant = getParticipantById(session, participantId);
    if (!participant) {
        return null;
    }
    return computeParticipantNameWithMe(t, participant);
};

const getParticipantNameById = (session: Session<AnyParticipant>, participantId: string) => {
    const participant = getParticipantById(session, participantId);
    if (!participant) {
        return null;
    }
    return computeParticipantName(participant);
};

const getParticipantNameWithMe = (t: TFunction, data: {firstName?: string, lastName?: string}, isMe = false) =>
    appendMeToName(t, getParticipantName(data), isMe);

const isInSession = (session: OnlineSession) => {
    const localUser = userAuthUtils.getLocalUser();
    return !!getParticipant(session, {eid: localUser.eid});
};

const isParticipantLocalUser = (participant: AnyParticipant) => {
    const localUser = userAuthUtils.getLocalUser();
    return !!getParticipant({participants: [participant]}, {eid: localUser.eid});
};

const isUserAssistantInSession = (session: OnlineSession, eid: number = userAuthUtils.getLocalUser().eid) =>
    !isEmpty(session.participants) && some(session.participants, (p) => p.role === ParticipantRole.Assistant && p.data.eid === eid);

const isUserAgentInSession = (session: OnlineSession, eid: number = userAuthUtils.getLocalUser().eid) =>
    !isEmpty(session.participants) && some(session.participants, (p) => p.role === ParticipantRole.Agent && p.data.eid === eid);

const canJoinSession = (session: OnlineSession, assistantEnabled: boolean) => {
    const hasAgentRole = !assistantEnabled || userAuthUtils.localUserHasInSessionRole(UserParticipantRole.Agent);
    if (session.status === SessionStatus.New) {
        const activeAgent = getAgentParticipant(session);
        return isInSession(session) || (hasAgentRole && !(activeAgent && !activeAgent.removed));
    }
    const allowedStatuses = hasAgentRole ? [SessionStatus.Calling] : [];
    const agent = getAgentParticipant(session);
    const isSessionTaken = agent && !agent.removed;
    return (allowedStatuses.includes(session?.status) && !isSessionTaken) || isInSession(session);
};

const goToSession = (joinLink?: string) => {
    if (joinLink) {
        window.location.href = joinLink;
    } else {
        history.push({
            pathname: ROUTE_PATH.USER.CONFERENCE._SELF,
            search: history.location.search
        });
    }
};

const goToAssistantSession = (params: {joinLink?: string, sessionId?: string}) => {
    if (params?.joinLink) {
        window.location.href = params.joinLink;
    } else if (params?.sessionId) {
        history.push({
            pathname: transformUtils.substituteLink(ROUTE_PATH.USER.CONFERENCE.ASSISTANT, params),
            search: history.location.search
        });
    }
};

const goToJoinSessionPage = (session: OnlineSession) => {
    const localUser = userAuthUtils.getLocalUser();
    const participant = getParticipant(session, {eid: localUser?.eid});
    history.push({
        pathname: transformUtils.substituteLink(ROUTE_PATH.SESSION.JOIN._SELF, {
            sessionId: session.id, role: participant?.role || UserParticipantRole.Agent
        }),
        search: history.location.search
    });
};

const getParticipantJoinSessionLink = (
    session: Session<AnyParticipant>,
    participant: AnyParticipant
): string => {
    const host = window.location.origin;
    let link;
    if (participant.role === ParticipantRole.Agent) {
        link = `/session/join/agent/${session.id}`;
    } else if (participant.role === ParticipantRole.Client) {
        link = `/client/join/${session.inviteClientParams?.code}/${session.inviteClientParams?.pin}`;
    } else if (participant.role === ParticipantRole.Assistant) {
        link = `/session/join/assistant/${session.id}`;
    } else if (participant.role === ParticipantRole.Guest) {
        const guest = participant as GuestParticipant;
        link = `/guest/join/${guest.data.inviteParams.code}/${guest.data.inviteParams.pin}`;
    }
    return host + link;
};

const canManageSession = (user: User = userAuthUtils.getLocalUser(), assistantEnabled?: boolean) =>
    !assistantEnabled || includes(user.inSessionRoles, UserParticipantRole.Agent);

const joinSession = async (session: OnlineSession, assistantEnabled: boolean) => {
    if (!canJoinSession(session, assistantEnabled)) {
        throw new Error('Cannot join session');
    }

    onlineSessionsStore.mergeValue({joiningSessionId: session.id});

    try {
        await agentActionsApi.joinSession(session?.id);
        goToSession();
    } finally {
        onlineSessionsStore.mergeValue({joiningSessionId: null});
    }
};

const getSessionStatus = (session: OnlineSession): SessionStatus => {
    const {joiningSessionId} = onlineSessionsStore.getValue();

    if (joiningSessionId === session.id) {
        return SessionStatus.InSession;
    }

    if ([SessionStatus.AgentsUnavailable, SessionStatus.AfterHours].includes(session.prevStatus)) {
        return session.prevStatus;
    }

    return session.status;
};

// TODO: Refactor session history table to use these methods
export const getFullSessionResultFormatted = (t: TFunction, session: {statuses: SessionStatus[], result: SessionResult}): string => {
    let result = getSessionResult(t, session);
    if (!result) {
        return null;
    }
    if (session.result.status && session.result.information) {
        result += ` (${session.result.information})`;
    }
    return result;
};

const canMakeAsyncRequest = (_participant: AnyParticipant, type: AsyncRequestTypeName): boolean => {
    const asyncRequest = find(_participant.asyncRequests, {type});
    const statusName = asyncRequest?.status?.name;
    return asyncRequest && ![AsyncRequestStatusName.Requested, AsyncRequestStatusName.InProgress].includes(statusName) && (
        !statusName || statusName === AsyncRequestStatusName.Failed
    );
};

const getParticipantLastActivity = ({dynamicData}: {dynamicData: SessionDynamicData}, participantId: string) =>
    dynamicData?.participants?.[participantId]?.lastActivity;

const getActiveUserSession = (
    sessions: OnlineSession[] = onlineSessionsStore.getValue().sessions,
    role: ParticipantRole = ParticipantRole.Agent
): OnlineSession =>
    find(sessions, (s) => AGENT_ACTIVE_SESSION_STATUSES.includes(s.status) &&
        some(s.participants, (p) => p.role === role && p.data.eid === userAuthUtils.getLocalUser().eid));

const getOnlineStatusFromLastActivity = (lastActivityDate: string, now: Date, awayAfter: number, offlineAfter: number) => {
    const diff = -moment(lastActivityDate).diff(now, 's');
    if (diff < awayAfter) {
        return OnlineStatus.Online;
    } else if (diff >= awayAfter && diff <= offlineAfter) {
        return OnlineStatus.Away;
    }
    return OnlineStatus.Offline;
};

const getOnlineStatus = (
    session: AgentSession | OnlineSession | SessionAppointment,
    now: Date,
    participant: AnyParticipant,
    lastActivityDate: string
): OnlineStatus => {
    if (participant?.socket?.date) {
        return participant.socket.connected && !participant?.left ? OnlineStatus.Online : OnlineStatus.Offline;
    }

    const isSipOrPhoneClient = [ClientDevice.Phone, ClientDevice.Sip].includes(participant.data.device);
    if (participant.role === ParticipantRole.Client && isSipOrPhoneClient) {
        if (participant?.left) {
            return OnlineStatus.Offline;
        }
        return OnlineStatus.Online;
    }

    if (participant?.left || !lastActivityDate) {
        return OnlineStatus.Offline;
    }
    return getOnlineStatusFromLastActivity(lastActivityDate, now, AWAY_AFTER, OFFLINE_AFTER);
};

/**
 * @deprecated should be removed
 */
const getUserInfoFieldsPath = (options?: {pathPrefix?: string}) => {
    const withPrefix = (path: string) => options?.pathPrefix ? `${options.pathPrefix}.${path}` : path;
    return {
        ID: withPrefix('id'),
        FIRST_NAME: withPrefix('firstName'),
        LAST_NAME: withPrefix('lastName'),
        EMAIL: withPrefix('email'),
        PHONE: withPrefix('phone'),
        CONTROL_ONLY_INFO: withPrefix('controlOnlyInfo'),
    };
};

const isClientDeviceSDK = (device: ClientDevice) =>
    [ClientDevice.AndroidSDK, ClientDevice.IosSDK].includes(device);

const computeParticipantEmail = (participant: AnyParticipant): string => {
    if (isInviteeParticipant(participant)) {
        return participant.data?.userInfo?.email;
    } else if (isUserParticipant(participant)) {
        return participant.data?.email;
    } else {
        return null;
    }
};

const getThreadsMaxIndex = (threads: TextChatThreads): number => {
    let maxIndex = 0;
    forEach(threads, (messages) => {
        const lastIndex = last(messages)?.index;
        if (isNumber(lastIndex) && lastIndex > maxIndex) {
            maxIndex = lastIndex;
        }
    });
    return maxIndex;
};

const addThreadsIndex = (threads: TextChatThreads, index: number): TextChatThreads => {
    const resultThreads = {};
    for (const participantId in threads) {
        const messages = map(threads[participantId], (message) => ({...message, index: message.index + index}));
        resultThreads[participantId] = messages;
    }
    return resultThreads;
};

const getMessagesFromThreadsArray = (threadsArray: TextChatThreads[]): TextChatMessage[] => {
    const result = [];
    const sortedActivitiesThreads = sortBy(threadsArray, (threads) => new Date(Object.values(threads)?.[0]?.[0]?.date));

    let maxIndex = 0;
    const prevActivitiesThreadsWithIndex = map(sortedActivitiesThreads, (threads: TextChatThreads) => {
        const resultThreads = addThreadsIndex(threads, maxIndex);
        maxIndex = getThreadsMaxIndex(threads);
        return resultThreads;
    });

    forEach(prevActivitiesThreadsWithIndex, (threads) => {
        for (const participantId in threads) {
            forEach(threads[participantId], (message) => {
                result.push({...message, from: {id: participantId}});
            });
        }
    });

    return sortBy(uniqBy(result, 'id'), ['index', 'date']);
};

const getSessionHistoryTextChatMessages = (session: SessionHistory): TextChatMessage[] => {
    const textChatActivities = filter(session.activities, (activity) => activity.name === SessionActivityName.TextChat);
    const allThreads = transform(textChatActivities, (acc, activity) => {
        const activityData = activity.data as SessionActivityTextChatData;
        if (!isEmpty(activityData?.threads)) {
            acc.push(activityData.threads);
        }
    }, [] as TextChatThreads[]);

    return getMessagesFromThreadsArray(allThreads);
};

const getPrevTextChatMessages = (textChatActivityData: SessionActivityTextChatData): TextChatMessage[] => {
    const prevActivitiesThreads = transform(textChatActivityData?.prevActivitiesData, (acc, prevData) => {
        acc.push(prevData.threads);
    }, [] as TextChatThreads[]);
    return getMessagesFromThreadsArray(prevActivitiesThreads);
};

const getTextChatActivityMessages = (sessionThreads: TextChatThreads = {}, socketThreads: TextChatThreads = {}): TextChatMessage[] => {
    const result = [];
    const mergedThreads = mergeWith({}, sessionThreads, socketThreads, (objValue, srcValue) => {
        if (isArray(objValue)) {
            return objValue.concat(srcValue);
        }
    });
    keys(mergedThreads).forEach((key) => mergedThreads[key].forEach((msg) => result.push({...msg, from: {id: key}})));

    return sortBy(uniqBy(result, 'id'), ['index', 'date']);
};

const getTextChatMessageOwner = (participantId: string, localParticipantId?: string): SessionTextChatMessageOwner => {
    if (participantId === DefaultParticipant.Chatbot) {
        return SessionTextChatMessageOwner.Chatbot;
    } else if (participantId === DefaultParticipant.System) {
        return SessionTextChatMessageOwner.System;
    } else if (participantId === localParticipantId) {
        return SessionTextChatMessageOwner.Own;
    } else {
        return SessionTextChatMessageOwner.Foreign;
    }
};

const getParticipantVideoSelector = (participantId: string) => `[${DATA_PARTICIPANT_ID}="${participantId}"]`;

export const sessionUtils = {
    getParticipantLastActivity,
    getVideoMinimized,
    canJoinSession,
    joinSession,
    goToSession,
    goToAssistantSession,
    getGuestParticipant,
    getGuestParticipants,
    getSessionStatus,
    getTextChatActivityMessages,
    getSessionHistoryTextChatMessages,
    getPrevTextChatMessages,
    getTextChatMessageOwner,
    getParticipant,
    getParticipantById,
    canMakeAsyncRequest,
    isUserParticipant,
    sortParticipantList,
    getParticipantName,
    getParticipantNames,
    getParticipantNameWithMe,
    computeParticipantNameWithMe,
    getParticipantNameWithMeById,
    getParticipantNameById,
    getAgentParticipant,
    getClientParticipant,
    getActiveUserSession,
    isInSession,
    isParticipantLocalUser,
    getOnlineStatus,
    getInitials,
    isUserAssistantInSession,
    getUserParticipantIdGroups,
    isUserAgentInSession,
    canManageSession,
    getUserInfoFieldsPath,
    isClientDeviceSDK,
    computeParticipantEmail,
    filterWebRTCActivitiesBySource,
    hasActivityWithAudio,
    goToJoinSessionPage,
    getParticipantVideoSelector,
    isInviteeParticipant,
    getParticipantJoinSessionLink
};
